import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, Package, MapPin, CreditCard, Loader, CheckCircle, AlertCircle, X, Phone, User, Mail, Sun, Moon } from 'lucide-react';
import { expeditionAPI, cashoutPALpaiementSansContactAPI, carrierAPI, relayAPI } from '../services/api';
import { Button, Input, Modal } from '../components/ui';
import { useTheme } from '../contexts/ThemeContext';

const initialAddress = {
  name: 'Non renseigné',
  phone: 'Non renseigné',
  email: 'Non renseigné',
  address: 'Non renseigné',
  company: ''
};

const extractAddressInfo = (addressObj) => {
  if (!addressObj) return initialAddress;

  if (typeof addressObj === 'string') {
    return {
      name: addressObj,
      phone: 'Non renseigné',
      email: 'Non renseigné',
      address: addressObj,
      company: ''
    };
  }

  if (typeof addressObj === 'object') {
    return {
      name: `${addressObj.customer_first_name || ''} ${addressObj.customer_last_name || ''}`.trim() ||
        `${addressObj.recipient_first_name || ''} ${addressObj.recipient_last_name || ''}`.trim() ||
        addressObj.name || 'Non renseigné',
      phone: addressObj.customer_phone_number || addressObj.pickup_phone_number || 'Non renseigné',
      email: addressObj.customer_email || addressObj.recipient_email || addressObj.pickup_email || addressObj.email || 'Non renseigné',
      address: addressObj.address || addressObj.full_address || 'Non renseigné',
      company: addressObj.pickup_company_name || addressObj.company_name || ''
    };
  }

  return initialAddress;
};

const PaiementSansContact = () => {
  const { isDark, toggleTheme } = useTheme();
  const [withdrawalCode, setWithdrawalCode] = useState('');
  const [searchError, setSearchError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expedition, setExpedition] = useState(null);
  const [carrierName, setCarrierName] = useState('');
  const [relayName, setRelayName] = useState('');
  
  // États pour le modal de paiement
  const [paymentKind, setPaymentKind] = useState('pal'); // 'pal' | 'livraison'
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentPhoneNumber, setPaymentPhoneNumber] = useState('');
  const [paymentCountryCode, setPaymentCountryCode] = useState('+225');
  const [paymentStep, setPaymentStep] = useState('idle');
  const [paymentError, setPaymentError] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [paymentSignature, setPaymentSignature] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // États pour les méthodes de paiement PAL
  const [palPaymentMethods, setPalPaymentMethods] = useState([]);
  const [selectedPALPaymentMethod, setSelectedPALPaymentMethod] = useState(null);
  const [isLoadingPALPaymentMethods, setIsLoadingPALPaymentMethods] = useState(false);
  const [palPaymentMethodsError, setPalPaymentMethodsError] = useState(null);
  
  // Ref pour l'interval de vérification du paiement PAL
  const palPaymentCheckIntervalRef = useRef(null);

  // 🌍 FONCTION : Détecter automatiquement le pays de l'utilisateur via IP
  const detectUserCountry = useCallback(async () => {
    try {
      console.log('🌍 Détection automatique du pays via IP...');
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error(`Erreur détection pays: ${response.status}`);
      }
      const data = await response.json();
      console.log('✅ Données géolocalisation IP:', data);
      const detectedCountry = data.country_code;
      console.log('🌍 Pays détecté via IP:', detectedCountry);
      
      // ✅ Si le pays détecté n'est pas en Afrique/supporté, utiliser CI par défaut
      const SUPPORTED_AFRICAN_CODES = ['CI', 'KE', 'BF', 'GN', 'GA', 'ML', 'SN', 'BJ', 'CM'];
      const countryCode = SUPPORTED_AFRICAN_CODES.includes(detectedCountry) ? detectedCountry : 'CI';
      
      // Mapping des codes pays vers les indicatifs téléphoniques
      const COUNTRY_TO_PHONE_CODE = {
        'CI': '+225', // Côte d'Ivoire
        'CM': '+237', // Cameroun
        'BF': '+226', // Burkina Faso
        'GN': '+224', // Guinée
        'GA': '+241', // Gabon
        'ML': '+223', // Mali
        'SN': '+221', // Sénégal
        'BJ': '+229', // Bénin
        'KE': '+254'  // Kenya
      };
      
      const phoneCode = COUNTRY_TO_PHONE_CODE[countryCode] || '+225';
      console.log('📞 Code téléphone détecté:', phoneCode);
      
      // Mettre à jour le code pays du téléphone
      setPaymentCountryCode(phoneCode);
      
      return countryCode;
    } catch (error) {
      console.error('❌ Erreur détection pays via IP:', error);
      try {
        console.log('🔄 Tentative avec ipinfo.io...');
        const fallbackResponse = await fetch('https://ipinfo.io/json');
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          console.log('✅ Données fallback ipinfo.io:', fallbackData);
          const detected = fallbackData.country;
          const SUPPORTED_AFRICAN_CODES = ['CI', 'KE', 'BF', 'GN', 'GA', 'ML', 'SN', 'BJ', 'CM'];
          const countryCode = SUPPORTED_AFRICAN_CODES.includes(detected) ? detected : 'CI';
          
          const COUNTRY_TO_PHONE_CODE = {
            'CI': '+225',
            'CM': '+237',
            'BF': '+226',
            'GN': '+224',
            'GA': '+241',
            'ML': '+223',
            'SN': '+221',
            'BJ': '+229',
            'KE': '+254'
          };
          
          const phoneCode = COUNTRY_TO_PHONE_CODE[countryCode] || '+225';
          setPaymentCountryCode(phoneCode);
          
          return countryCode;
        }
      } catch (fallbackError) {
        console.error('❌ Erreur fallback ipinfo.io:', fallbackError);
      }
      return 'CI';
    }
  }, []);

  // 🎯 Fonction pour normaliser le code opérateur (retourne OM, WAVE, MTN, MOOV, etc.)
  const normalizeOperatorCode = (operator) => {
    if (!operator) return 'OM';
    const operatorLower = operator.toLowerCase();
    if (operatorLower.includes('wave') || operatorLower.includes('free') || operatorLower.includes('emoney') || operatorLower.includes('freemoney')) return 'WAVE';
    if (operatorLower.includes('orange') || operatorLower.includes('om')) return 'OM';
    if (operatorLower.includes('mtn') || operatorLower.includes('airtel')) return 'MTN';
    if (operatorLower.includes('moov')) return 'MOOV';
    if (operatorLower.includes('visa') || operatorLower.includes('mastercard')) return 'CARD';
    return 'OM'; // Par défaut
  };

  // 🎯 Icône selon opérateur PAL
  const getPALPaymentIcon = (operator) => {
    if (!operator) return '/OM.png';
    const operatorLower = operator.toLowerCase();
    if (operatorLower.includes('wave')) return '/WAVE.png';
    if (operatorLower.includes('orange') || operatorLower.includes('om')) return '/OM.png';
    if (operatorLower.includes('mtn')) return '/MTN.png';
    if (operatorLower.includes('moov')) return '/MOOV.png';
    if (operatorLower.includes('free')) return '/WAVE.png';
    if (operatorLower.includes('emoney')) return '/WAVE.png';
    if (operatorLower.includes('freemoney')) return '/WAVE.png';
    if (operatorLower.includes('airtel')) return '/MTN.png';
    if (operatorLower.includes('visa')) return '/OM.png';
    if (operatorLower.includes('mastercard')) return '/OM.png';
    return '/OM.png';
  };

  // 🎯 Type de paiement PAL
  const getPALPaymentType = (operator) => {
    if (!operator) return 'autre';
    const operatorLower = operator.toLowerCase();
    if (
      operatorLower.includes('wave') ||
      operatorLower.includes('orange') ||
      operatorLower.includes('mtn') ||
      operatorLower.includes('moov') ||
      operatorLower.includes('free') ||
      operatorLower.includes('emoney') ||
      operatorLower.includes('freemoney') ||
      operatorLower.includes('airtel')
    ) {
      return 'mobile_money';
    }
    if (operatorLower.includes('visa') || operatorLower.includes('mastercard')) {
      return 'card';
    }
    return 'autre';
  };

  // 💳 FONCTION : Charger les modes de paiement PAL disponibles
  const loadPALPaymentMethods = useCallback(async () => {
    setIsLoadingPALPaymentMethods(true);
    try {
      console.log('🔍 Chargement des modes de paiement PAL via API Clapay...');
      // 1️⃣ Détecter automatiquement le pays de l'utilisateur via IP
      let userCountry = await detectUserCountry();
      console.log('🌍 Pays utilisateur détecté pour PAL:', userCountry);

      // 2️⃣ Mapping potentiels (si l'API retourne des noms au lieu de codes)
      const COUNTRY_MAPPING = {
        "Côte d'Ivoire": 'CI',
        'Kenya': 'KE',
        'Burkina Faso': 'BF',
        'Guinée Conakry': 'GN',
        'Gabon': 'GA',
        'Mali': 'ML',
        'Sénégal': 'SN',
        'Bénin': 'BJ',
        'Cameroun': 'CM'
      };
      const countryCode = COUNTRY_MAPPING[userCountry] || userCountry;
      console.log('🌍 Code pays utilisé pour PAL:', countryCode);

      // 3️⃣ Appeler l'API Clapay
      const clapayUrl = `https://nowallet-api.mpayment.africa/nowallet/api/fees/by/country?country=${countryCode}`;
      console.log('🔗 URL API Clapay PAL:', clapayUrl);
      const response = await fetch(clapayUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: 'Bearer d41c1b19dce70da75aa3701887dff1aa14f2b7e082a700b2aa94e1427d7ec3e01b8e83090e89e41430161d46e2fbbbaa4c048285625d6dbacafbc8c6efaf5cd674d2e0b3df0181a6492abfd3f9e560b0707a89bb98dfe542f0aea0e59908763b'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur API Clapay PAL: ${response.status} ${response.statusText}`);
      }

      const clapayData = await response.json();
      console.log('✅ Données API Clapay PAL reçues:', clapayData);

      if (Array.isArray(clapayData)) {
        const transformedMethods = clapayData.map((method, index) => {
          const operatorCode = normalizeOperatorCode(method.operator);
          return {
            id: method.id || index + 1,
            nom: method.operator || method.description || 'Méthode de paiement',
            icone: getPALPaymentIcon(method.operator),
            description: method.description || `${method.operator} - ${method.currency}`,
            montant_min: 0,
            montant_max: 999999999,
            type: getPALPaymentType(method.operator),
            operatorCode: operatorCode, // Code normalisé (OM, WAVE, MTN, MOOV, etc.)
            clapayData: {
              fee_cashin: method.fee_cashin,
              fee_cashout: method.fee_cashout,
              fee_merchant: method.fee_merchant,
              currency: method.currency,
              country: method.country,
              merchant: method.merchant,
              operator: method.operator
            }
          };
        });
        console.log('🔄 Méthodes PAL transformées (Clapay → App):', transformedMethods);
        setPalPaymentMethods(transformedMethods);
        if (transformedMethods.length > 0 && !selectedPALPaymentMethod) {
          setSelectedPALPaymentMethod(transformedMethods[0]);
        }
        setPalPaymentMethodsError(null);
      } else {
        throw new Error('Format de réponse API Clapay PAL invalide');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des modes de paiement PAL Clapay:', error);
      setPalPaymentMethodsError(error.message);
      const defaultMethods = [
        { id: 1, nom: 'Orange Money', icone: '/OM.png', description: 'Paiement mobile Orange Money', type: 'mobile_money', operatorCode: 'OM', clapayData: { operator: 'OM' } },
        { id: 2, nom: 'MTN Mobile Money', icone: '/MTN.png', description: 'Paiement mobile MTN', type: 'mobile_money', operatorCode: 'MTN', clapayData: { operator: 'MTN' } },
        { id: 3, nom: 'WAVE', icone: '/WAVE.png', description: 'Paiement mobile WAVE', type: 'mobile_money', operatorCode: 'WAVE', clapayData: { operator: 'WAVE' } }
      ];
      setPalPaymentMethods(defaultMethods);
      setSelectedPALPaymentMethod(defaultMethods[0]);
    } finally {
      setIsLoadingPALPaymentMethods(false);
    }
  }, [detectUserCountry]);

  // Détecter le pays et charger les méthodes de paiement au chargement de la page
  useEffect(() => {
    detectUserCountry();
    loadPALPaymentMethods();
  }, [detectUserCountry, loadPALPaymentMethods]);

  // Nettoyer l'interval lors du démontage du composant
  useEffect(() => {
    return () => {
      if (palPaymentCheckIntervalRef.current) {
        clearInterval(palPaymentCheckIntervalRef.current);
        palPaymentCheckIntervalRef.current = null;
      }
    };
  }, []);

  // Recherche de l'expédition par code de retrait / n° Feli / commande
  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    const code = (withdrawalCode || '').trim();

    if (!code) {
      setSearchError('Veuillez saisir un code de retrait ou un numéro d\'expédition');
      return;
    }

    setIsLoading(true);
    setSearchError('');
    setExpedition(null);

    try {
      const response = await expeditionAPI.ExpeditionaPAL(code);

      if (response?.data) {
        const data = response.data;
        const profile = data.payment_profile || {};
        const matchesCode = [
          data.code_retrait,
          data.withdrawal_code,
          data.expedition_number,
          data.order_number,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase() === code.toLowerCase());

        if (!matchesCode) {
          setSearchError('Aucune expédition trouvée avec ce code');
          setIsLoading(false);
          return;
        }

        setExpedition({ ...data, payment_profile: profile });

        try {
          const [carrierResponse, relayResponse] = await Promise.all([
            carrierAPI.getAllCarriers(),
            relayAPI.getAllRelays()
          ]);

          const carrier = carrierResponse?.data?.find((item) => item.id === data.transporteur);
          setCarrierName(carrier ? carrier.nom : `Transporteur ${data.transporteur || '-'}`);

          const relay = relayResponse?.data?.find((item) => item.id === data.pointrelais);
          setRelayName(relay ? relay.nom : `Point relais ${data.pointrelais || '-'}`);
        } catch (innerError) {
          console.error('Erreur récupération transporteur / point relais:', innerError);
          setCarrierName(`Transporteur ${data.transporteur || '-'}`);
          setRelayName(`Point relais ${data.pointrelais || '-'}`);
        }
      } else {
        setSearchError('Aucune expédition trouvée avec ce code de retrait');
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      // Afficher exactement le message d'erreur retourné par le backend
      if (error.response?.data?.error) {
        setSearchError(error.response.data.error);
      } else if (error.response?.data?.message) {
        setSearchError(error.response.data.message);
      } else if (error.response?.status === 404) {
        setSearchError('Aucune expédition trouvée avec ce code de retrait');
      } else if (error.message) {
        setSearchError(error.message);
      } else {
        setSearchError('Erreur lors de la récupération des informations. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [withdrawalCode]);

  const refreshExpedition = useCallback(async () => {
    const code = (withdrawalCode || '').trim();
    if (!code) return;
    try {
      const response = await expeditionAPI.ExpeditionaPAL(code);
      if (response?.data) {
        const data = response.data;
        setExpedition({ ...data, payment_profile: data.payment_profile || {} });
      }
    } catch (error) {
      console.error('Erreur rafraîchissement expédition:', error);
    }
  }, [withdrawalCode]);

  const handleOpenPaymentModal = (kind) => {
    setPaymentKind(kind);
    setShowPaymentModal(true);
    setPaymentStep('idle');
    setPaymentError('');
    setPaymentPhoneNumber('');
    setTransactionId(null);
    setPaymentSignature(null);
    setPaymentUrl(null);
    setPaymentStatus(null);
    // Charger les méthodes de paiement si pas déjà chargées
    if (palPaymentMethods.length === 0) {
      loadPALPaymentMethods();
    }
  };

  const handleInitPayment = async () => {
    if (!paymentPhoneNumber.trim()) {
      setPaymentError('Veuillez saisir un numéro de téléphone');
      return;
    }

    if (!selectedPALPaymentMethod) {
      setPaymentError('Veuillez sélectionner une méthode de paiement');
      return;
    }

    if (!expedition) {
      setPaymentError('Aucune expédition sélectionnée');
      return;
    }

    setPaymentStep('processing');
    setPaymentError('');

    try {
      const phoneDigits = paymentPhoneNumber.replace(/\D/g, '');
      
      // Mapping du code téléphonique vers le code ISO du pays
      const PHONE_CODE_TO_COUNTRY = {
        '+225': 'CI', // Côte d'Ivoire
        '+237': 'CM', // Cameroun
        '+226': 'BF', // Burkina Faso
        '+224': 'GN', // Guinée
        '+241': 'GA', // Gabon
        '+223': 'ML', // Mali
        '+221': 'SN', // Sénégal
        '+229': 'BJ', // Bénin
        '+254': 'KE'  // Kenya
      };
      
      // Obtenir le code ISO du pays depuis le code téléphonique
      const countryCode = PHONE_CODE_TO_COUNTRY[paymentCountryCode] || 'CI';
      
      // Obtenir le code opérateur normalisé depuis la méthode de paiement sélectionnée
      const operatorCode = selectedPALPaymentMethod.operatorCode || 
                          normalizeOperatorCode(selectedPALPaymentMethod.clapayData?.operator || selectedPALPaymentMethod.nom) || 
                          'OM';

      const profile = expedition.payment_profile || {};
      const isPal = paymentKind === 'pal';
      const amount = isPal
        ? (profile.pal?.amount ?? expedition.montantpal ?? 0)
        : (profile.montant_livraison ?? expedition.montant ?? 0);
      
      const paymentData = {
        amount,
        phone_number: phoneDigits,
        country_code: countryCode,
        operators_code: operatorCode,
        expedition_id: expedition.id,
        code_retrait: expedition.code_retrait || expedition.withdrawal_code,
      };

      console.log(`💳 Initialisation paiement ${isPal ? 'PAL' : 'livraison'}:`, paymentData);

      const response = isPal
        ? await cashoutPALpaiementSansContactAPI.initcashoutPALpaimement(paymentData)
        : await cashoutPALpaiementSansContactAPI.initLivraisonSansContact(paymentData);
      
      console.log('✅ Réponse initialisation paiement PAL:', response.data);
      
      if (response?.data) {
        // Sauvegarder la signature pour vérifier le statut
        const signature = response.data.signature;
        if (signature) {
          setPaymentSignature(signature);
          // Démarrer la vérification du statut avec la signature
          startPALPaymentStatusCheck(signature);
        }
        
        // Sauvegarder l'URL de paiement si disponible
        const paymentUrl = response.data.payment_url || response.data.url;
        if (paymentUrl) {
          setPaymentUrl(paymentUrl);
          // Ouvrir l'URL de paiement dans un nouvel onglet
          window.open(paymentUrl, '_blank');
        }
        
        // Sauvegarder aussi le transaction_id si disponible
        const transactionId = response.data.transaction_id || response.data.id;
        if (transactionId) {
          setTransactionId(transactionId);
        }
      } else {
        throw new Error('Réponse invalide de l\'API');
      }
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du paiement:', error);
      setPaymentError(error.response?.data?.message || error.message || 'Erreur lors de l\'initialisation du paiement');
      setPaymentStep('error');
    }
  };

  // 🔄 Vérifier le statut du paiement PAL
  const checkPALPaymentStatus = async (signature) => {
    try {
      console.log('🔍 Vérification du statut de paiement PAL pour la signature:', signature);
      const response = await cashoutPALpaiementSansContactAPI.paiementcheckin({ signature });
      console.log('✅ Réponse de vérification du paiement PAL:', response.data);
      const paymentData = response.data;

      if (paymentData.status === 'SUCCESSFUL') {
        console.log('🎉 Paiement confirmé avec succès !');
        if (palPaymentCheckIntervalRef.current) {
          clearInterval(palPaymentCheckIntervalRef.current);
          palPaymentCheckIntervalRef.current = null;
        }
        setPaymentStep('success');
        await refreshExpedition();
        // Confirmer le paiement si transaction_id disponible
        const txId = paymentData.transaction_id || transactionId;
        if (txId) {
          try {
            await cashoutPALpaiementSansContactAPI.paiementConfirmation(txId);
          } catch (confirmError) {
            console.error('Erreur lors de la confirmation:', confirmError);
          }
        }
      } else if (paymentData.status === 'FAILED' || paymentData.status === 'CLOSED') {
        console.log('❌ Paiement PAL échoué ou annulé');
        if (palPaymentCheckIntervalRef.current) {
          clearInterval(palPaymentCheckIntervalRef.current);
          palPaymentCheckIntervalRef.current = null;
        }
        setPaymentStep('error');
        setPaymentError(paymentData.status === 'CLOSED' ? 'Paiement annulé' : 'Le paiement a échoué');
      } else {
        console.log('⏳ Paiement PAL encore en cours, statut:', paymentData.status);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du paiement PAL:', error);
      // En cas d'erreur, continuer la vérification (peut-être que le paiement n'est pas encore traité)
    }
  };

  // 🚀 Démarrer la vérification du statut de paiement PAL
  const startPALPaymentStatusCheck = (signature) => {
    // Arrêter tout timer existant
    if (palPaymentCheckIntervalRef.current) {
      clearInterval(palPaymentCheckIntervalRef.current);
    }
    
    // Faire une première vérification immédiate
    checkPALPaymentStatus(signature);
    
    // Démarrer un nouveau timer pour vérifier le statut toutes les 3 secondes
    palPaymentCheckIntervalRef.current = setInterval(() => {
      checkPALPaymentStatus(signature);
    }, 3000);
    
    console.log('🔄 Vérification du statut de paiement PAL démarrée');
  };

  const senderInfo = expedition ? extractAddressInfo(expedition.adresse_expediteur) : initialAddress;
  const recipientInfo = expedition ? extractAddressInfo(expedition.adresse_destinataire) : initialAddress;
  const parcels = expedition?.infocolis || [];
  const paymentProfile = expedition?.payment_profile;
  const canPayPalOnline = paymentProfile?.pal?.can_pay_online === true;
  const canPayLivraisonOnline = paymentProfile?.livraison?.can_pay_online === true;
  const palAmount = paymentProfile?.pal?.amount ?? expedition?.montantpal;
  const livraisonAmount = paymentProfile?.montant_livraison ?? expedition?.montant;
  const livraisonProfile = paymentProfile?.livraison;
  const palProfile = paymentProfile?.pal;
  const paymentTraces = paymentProfile?.payment_traces || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-bg dark:to-dark-bg-secondary">
      <div className="min-h-screen flex flex-col">
        {/* En-tête KSL */}
        <header className="w-full py-6 px-4 sm:px-6 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-dark-bg-secondary/80 backdrop-blur-sm">
          <div className="container-ksl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/katian-logo.png" 
                  alt="Katian Service Logistique" 
                  className="h-10 w-auto"
                />
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    Paiement PAL
                  </h1>
                  <p className="text-xs sm:text-sm text-ksl-gray dark:text-ksl-gray-light">
                    Paiement sans contact KSL
                  </p>
                </div>
              </div>
              {/* Bouton toggle dark mode */}
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 border border-gray-200 dark:border-gray-700 shadow-sm"
                title={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
                aria-label={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-yellow-400" />
                ) : (
                  <Moon className="w-5 h-5 text-ksl-red" />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Contenu principal */}
        <main className="flex-1 w-full px-4 sm:px-6 py-8 sm:py-12">
          <div className="container-ksl max-w-4xl mx-auto">
            {/* Section de recherche */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-ksl-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-6">
              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Rechercher une expédition
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ksl-gray dark:text-ksl-gray-light" />
                      <Input
                        type="text"
                        value={withdrawalCode}
                        onChange={(e) => setWithdrawalCode(e.target.value)}
                        placeholder="Code retrait, n° FELI-… ou commande"
                        className="w-full pl-12 pr-4 py-3 text-base"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      isLoading={isLoading}
                      size="lg"
                      className="w-full sm:w-auto px-8"
                    >
                      {!isLoading && <Search className="w-5 h-5 mr-2" />}
                      {isLoading ? 'Recherche...' : 'Rechercher'}
                    </Button>
                  </div>
                </div>
                {searchError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-ksl-red dark:text-ksl-red-light flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-ksl-red dark:text-ksl-red-light">{searchError}</p>
                  </div>
                )}
              </form>
            </div>

            {/* Affichage de l'expédition */}
            {expedition && (
              <div className="space-y-6 animate-fade-in">
                {/* Carte principale */}
                <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-ksl-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* En-tête avec montant - Gradient KSL */}
                  <div className="bg-gradient-to-br from-ksl-red via-ksl-red-dark to-ksl-black p-6 sm:p-8 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <p className="text-sm opacity-90 mb-1">
                          {canPayPalOnline ? 'Montant PAL à payer' : 'Marchandise (PAL)'}
                        </p>
                        <p className="text-3xl sm:text-4xl font-bold">
                          {palAmount ? `${palAmount} FCFA` : '—'}
                        </p>
                        {palProfile?.label && (
                          <p className="text-sm opacity-90 mt-2">{palProfile.label}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-90 mb-1">Code de retrait</p>
                        <p className="text-xl font-mono font-bold">{expedition.code_retrait || expedition.expedition_number || '—'}</p>
                        {paymentProfile?.is_feli_mobile && (
                          <p className="text-xs opacity-80 mt-1">Livraison Feli (app mobile)</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 sm:p-8 space-y-6">
                    {/* Profil de paiement */}
                    {paymentProfile && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                        <div>
                          <p className="text-xs text-ksl-gray dark:text-ksl-gray-light mb-1">Frais de livraison</p>
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            {paymentProfile.montant_livraison != null
                              ? `${paymentProfile.montant_livraison} FCFA`
                              : '—'}
                          </p>
                          <p className="text-sm text-ksl-gray dark:text-ksl-gray-light mt-1">
                            {livraisonProfile?.label || '—'}
                            {livraisonProfile?.mode_paiement
                              ? ` · ${livraisonProfile.mode_paiement}`
                              : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-ksl-gray dark:text-ksl-gray-light mb-1">Marchandise (PAL)</p>
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            {palProfile?.required
                              ? `${palProfile.amount} FCFA`
                              : 'Sans PAL'}
                          </p>
                          <p className="text-sm text-ksl-gray dark:text-ksl-gray-light mt-1">
                            {palProfile?.label || '—'}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Informations expédition */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-ksl-gray dark:text-ksl-gray-light mb-1">Numéro d'expédition</p>
                        <p className="text-base font-semibold text-gray-900 dark:text-white font-mono">
                          {expedition.expedition_number || '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-ksl-gray dark:text-ksl-gray-light mb-1">Type de service</p>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          {expedition.type_service || '—'}
                        </p>
                      </div>
                      {expedition.mode_expedition && (
                        <div>
                          <p className="text-xs text-ksl-gray dark:text-ksl-gray-light mb-1">Mode d'expédition</p>
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            {expedition.mode_expedition}
                          </p>
                        </div>
                      )}
                      {carrierName && (
                        <div>
                          <p className="text-xs text-ksl-gray dark:text-ksl-gray-light mb-1">Transporteur</p>
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            {carrierName}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Adresses */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-ksl-red dark:text-ksl-red-light">
                          <MapPin className="w-4 h-4" />
                          <h3 className="font-semibold text-sm uppercase tracking-wide">Expéditeur</h3>
                        </div>
                        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                          <p className="flex items-center gap-2">
                            <User className="w-4 h-4 text-ksl-gray dark:text-ksl-gray-light" />
                            <span className="font-medium">{senderInfo.name}</span>
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-ksl-gray dark:text-ksl-gray-light" />
                            <span>{senderInfo.phone}</span>
                          </p>
                          <p className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-ksl-gray dark:text-ksl-gray-light" />
                            <span>{senderInfo.email}</span>
                          </p>
                          <p className="text-ksl-gray dark:text-ksl-gray-light mt-2 pl-6">{senderInfo.address}</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-ksl-red dark:text-ksl-red-light">
                          <Package className="w-4 h-4" />
                          <h3 className="font-semibold text-sm uppercase tracking-wide">Destinataire</h3>
                        </div>
                        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                          {recipientInfo.company && (
                            <p className="font-medium text-gray-900 dark:text-white">{recipientInfo.company}</p>
                          )}
                          <p className="flex items-center gap-2">
                            <User className="w-4 h-4 text-ksl-gray dark:text-ksl-gray-light" />
                            <span className="font-medium">{recipientInfo.name}</span>
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-ksl-gray dark:text-ksl-gray-light" />
                            <span>{recipientInfo.phone}</span>
                          </p>
                          <p className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-ksl-gray dark:text-ksl-gray-light" />
                            <span>{recipientInfo.email}</span>
                          </p>
                          <p className="text-ksl-gray dark:text-ksl-gray-light mt-2 pl-6">{recipientInfo.address}</p>
                        </div>
                      </div>
                    </div>

                    {/* Boutons paiement — livraison et PAL séparément */}
                    <div className="pt-6 border-t border-gray-200 dark:border-gray-700 space-y-3">
                      {canPayLivraisonOnline && (
                        <Button
                          onClick={() => handleOpenPaymentModal('livraison')}
                          size="lg"
                          variant="outline"
                          className="w-full py-4 text-base font-semibold border-ksl-red text-ksl-red hover:bg-ksl-red/5"
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          Payer la livraison ({livraisonAmount} FCFA)
                        </Button>
                      )}
                      {canPayPalOnline && (
                        <Button
                          onClick={() => handleOpenPaymentModal('pal')}
                          size="lg"
                          className="w-full py-4 text-base font-semibold shadow-ksl"
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          Payer la marchandise PAL ({palAmount} FCFA)
                        </Button>
                      )}
                      {!canPayLivraisonOnline && !canPayPalOnline && (
                        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-100 space-y-1">
                          {livraisonProfile?.paid && (
                            <p>Frais de livraison : déjà réglés.</p>
                          )}
                          {livraisonProfile?.payment_in_progress && (
                            <p>Paiement livraison en cours de validation…</p>
                          )}
                          {!palProfile?.required && !canPayLivraisonOnline && (
                            <p>Cette expédition n&apos;a pas de montant PAL à régler en ligne.</p>
                          )}
                          {palProfile?.required && palProfile?.paid_online && (
                            <p>Le PAL a déjà été payé en ligne.</p>
                          )}
                          {palProfile?.required && palProfile?.collected_at_delivery && !palProfile?.paid_online && (
                            <p>Le PAL a déjà été encaissé à la livraison.</p>
                          )}
                          {palProfile?.payment_in_progress && (
                            <p>Paiement PAL en cours de validation…</p>
                          )}
                          {(canPayLivraisonOnline === false && canPayPalOnline === false && livraisonProfile?.pay_on_delivery_especes && !livraisonProfile?.paid) && (
                            <p className="text-xs opacity-80">
                              Vous pouviez régler la livraison en espèces ; si vous préférez payer en ligne, rechargez la page après mise à jour.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Historique des paiements */}
                    {paymentTraces.length > 0 && (
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                          Historique des paiements
                        </h4>
                        <div className="space-y-2">
                          {paymentTraces.map((trace) => (
                            <div
                              key={trace.id}
                              className="flex flex-wrap items-center justify-between gap-2 text-xs p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60"
                            >
                              <div>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {trace.label}
                                </span>
                                {trace.amount != null && (
                                  <span className="text-ksl-gray dark:text-ksl-gray-light ml-2">
                                    {trace.amount} FCFA
                                  </span>
                                )}
                              </div>
                              <span
                                className={`px-2 py-0.5 rounded-full font-medium ${
                                  trace.status === 'SUCCESSFUL'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                }`}
                              >
                                {trace.status || 'En cours'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Détails des colis (optionnel) */}
                {parcels.length > 0 && (
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-ksl-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Package className="w-5 h-5 text-ksl-red" />
                      Détails des colis ({parcels.length})
                    </h3>
                    <div className="space-y-3">
                      {parcels.map((colis, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {colis.name || `Colis ${index + 1}`}
                            </h4>
                            {colis.category && (
                              <span className="text-xs bg-ksl-red/10 text-ksl-red dark:bg-ksl-red/20 dark:text-ksl-red-light px-2 py-1 rounded-full">
                                {colis.category}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-ksl-gray dark:text-ksl-gray-light">
                            <div>Poids: {colis.weight || '--'} kg</div>
                            <div>Qté: {colis.quantity || '--'}</div>
                            <div>Dimensions: {colis.length || '--'}×{colis.width || '--'}×{colis.height || '--'} cm</div>
                            <div>
                              Volume: {colis.length && colis.width && colis.height 
                                ? ((colis.length * colis.width * colis.height) / 1000000).toFixed(2) 
                                : '--'} m³
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        {/* Footer minimaliste KSL */}
        <footer className="w-full py-4 px-4 sm:px-6 border-t border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-dark-bg-secondary/80 backdrop-blur-sm">
          <div className="container-ksl text-center text-sm text-ksl-gray dark:text-ksl-gray-light">
            Paiement sécurisé • <span className="font-semibold text-ksl-red">KSL</span> Katian Service Logistique
          </div>
        </footer>
      </div>

      {/* Modal de paiement */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => {
          if (paymentStep !== 'processing') {
            // Nettoyer l'interval si le modal est fermé
            if (palPaymentCheckIntervalRef.current) {
              clearInterval(palPaymentCheckIntervalRef.current);
              palPaymentCheckIntervalRef.current = null;
            }
            setShowPaymentModal(false);
          }
        }}
        size="max-w-md"
        title={paymentKind === 'pal' ? 'Paiement PAL (marchandise)' : 'Paiement livraison'}
      >
        <div className="space-y-6">
          {/* Résumé */}
          {expedition && (
            <div className="bg-gradient-to-r from-ksl-red/10 to-ksl-red-dark/10 dark:from-ksl-red/20 dark:to-ksl-red-dark/20 rounded-xl p-4 border border-ksl-red/20">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ksl-gray dark:text-ksl-gray-light">Expédition:</span>
                  <span className="font-mono font-semibold text-gray-900 dark:text-white">{expedition.expedition_number}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-ksl-red/20">
                  <span className="text-gray-900 dark:text-white">
                    {paymentKind === 'pal' ? 'Montant PAL :' : 'Frais livraison :'}
                  </span>
                  <span className="text-ksl-red">
                    {paymentKind === 'pal'
                      ? (palAmount ? `${palAmount} FCFA` : '—')
                      : (livraisonAmount ? `${livraisonAmount} FCFA` : '—')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Formulaire de paiement */}
          {paymentStep === 'idle' && (
            <div className="space-y-4">
              {/* Sélection de la méthode de paiement */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Méthode de paiement
                </label>
                {isLoadingPALPaymentMethods ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader className="w-5 h-5 animate-spin text-ksl-red" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Chargement des méthodes...</span>
                  </div>
                ) : palPaymentMethods.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {palPaymentMethods.map((method) => (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setSelectedPALPaymentMethod(method)}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          selectedPALPaymentMethod?.id === method.id
                            ? 'border-ksl-red bg-ksl-red/10 dark:bg-ksl-red/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-ksl-red/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <img 
                            src={method.icone} 
                            alt={method.nom}
                            className="w-8 h-8 object-contain"
                            onError={(e) => { e.target.src = '/OM.png'; }}
                          />
                          <div className="flex-1 text-left">
                            <div className="font-semibold text-sm text-gray-900 dark:text-white">
                              {method.nom}
                            </div>
                            {method.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {method.description}
                              </div>
                            )}
                          </div>
                          {selectedPALPaymentMethod?.id === method.id && (
                            <CheckCircle className="w-5 h-5 text-ksl-red" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-red-600 dark:text-red-400">
                    Aucune méthode de paiement disponible
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Numéro de téléphone
                </label>
                <div className="flex gap-3">
                  <select
                    value={paymentCountryCode}
                    onChange={(e) => setPaymentCountryCode(e.target.value)}
                    className="w-28 px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ksl-red focus:border-transparent"
                  >
                    <option value="+225">🇨🇮 +225</option>
                    <option value="+237">🇨🇲 +237</option>
                    <option value="+226">🇧🇫 +226</option>
                    <option value="+224">🇬🇳 +224</option>
                    <option value="+241">🇬🇦 +241</option>
                    <option value="+223">🇲🇱 +223</option>
                    <option value="+221">🇸🇳 +221</option>
                    <option value="+229">🇧🇯 +229</option>
                  </select>
                  <div className="flex-1">
                    <Input
                      type="tel"
                      placeholder="Numéro de téléphone"
                      value={paymentPhoneNumber}
                      onChange={(e) => setPaymentPhoneNumber(e.target.value)}
                      className="w-full py-3"
                      required
                    />
                  </div>
                </div>
              </div>

              {paymentError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-ksl-red dark:text-ksl-red-light flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-ksl-red dark:text-ksl-red-light">{paymentError}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleInitPayment}
                  disabled={!paymentPhoneNumber.trim() || !selectedPALPaymentMethod}
                  className="flex-1"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payer
                </Button>
              </div>
            </div>
          )}

          {/* Statut de traitement */}
          {paymentStep === 'processing' && (
            <div className="space-y-4 py-8">
              <div className="flex flex-col items-center justify-center space-y-4">
                <Loader className="w-12 h-12 animate-spin text-ksl-red" />
                <div className="text-center">
                  <p className="font-semibold text-gray-900 dark:text-white mb-2">
                    Redirection vers le portail de paiement...
                  </p>
                  <p className="text-sm text-ksl-gray dark:text-ksl-gray-light mb-3">
                    Une nouvelle fenêtre s'est ouverte. Veuillez compléter le paiement dans cette fenêtre.
                  </p>
                  {paymentUrl && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(paymentUrl, '_blank')}
                      className="mt-2"
                      size="sm"
                    >
                      Rouvrir le portail de paiement
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Succès */}
          {paymentStep === 'success' && (
            <div className="space-y-4 py-4">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Paiement réussi !
                  </p>
                  <p className="text-sm text-ksl-gray dark:text-ksl-gray-light">
                    Votre paiement a été effectué avec succès. L'expédition sera traitée sous peu.
                  </p>
                </div>
              </div>
              {transactionId && (
                <div className="text-center text-xs text-ksl-gray dark:text-ksl-gray-light bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                  ID Transaction : {transactionId}
                </div>
              )}
              <Button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentStep('idle');
                  setExpedition(null);
                  setWithdrawalCode('');
                }}
                className="w-full"
                size="lg"
              >
                Fermer
              </Button>
            </div>
          )}

          {/* Erreur */}
          {paymentStep === 'error' && (
            <div className="space-y-4 py-4">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-ksl-red dark:text-ksl-red-light" />
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Échec du paiement
                  </p>
                  {paymentError && (
                    <p className="text-sm text-ksl-red dark:text-ksl-red-light bg-red-50 dark:bg-red-900/20 rounded-xl p-3">
                      {paymentError}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Nettoyer l'interval si présent
                    if (palPaymentCheckIntervalRef.current) {
                      clearInterval(palPaymentCheckIntervalRef.current);
                      palPaymentCheckIntervalRef.current = null;
                    }
                    setShowPaymentModal(false);
                  }}
                  className="flex-1"
                >
                  Fermer
                </Button>
                <Button
                  onClick={() => {
                    // Nettoyer l'interval si présent
                    if (palPaymentCheckIntervalRef.current) {
                      clearInterval(palPaymentCheckIntervalRef.current);
                      palPaymentCheckIntervalRef.current = null;
                    }
                    setPaymentStep('idle');
                    setPaymentError('');
                  }}
                  className="flex-1"
                >
                  Réessayer
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PaiementSansContact;
